import Alert from '@amzn/awsui-components-core/alert';
import Badge from '@amzn/awsui-components-core/badge';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import HelpPanel from '@amzn/awsui-components-core/help-panel';
import Link from '@amzn/awsui-components-core/link';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Spinner from '@amzn/awsui-components-core/spinner';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStarGridData } from '../context/StarGridDataContext';
import { useReportSelectors } from '../hooks/useReportSelectors';
import { useStarGridPnlReport } from '../hooks/useStarGridPnlReport';
import PnlReportGrid from '../components/PnlReportGrid';
import ReportHealth from '../components/ReportHealth';
import { PNL_DEFAULT_SCALE } from '../utils/pnlGrid';
import { PNL_STATEMENT_LINE_DIMENSION } from '../starGridConstants';
import { useDataSyncText } from '../hooks/useDataSyncText';
import { ReportLayout } from '../components/ReportLayout';
import { ReportFilterBar } from '../components/ReportFilterBar';
import styles from './styles/ReportDetailView.module.scss';

export interface ReportDetailViewProps {
  reportId: number;
  /** Called after the report is deleted (parent refreshes the list + navigates away). */
  onDeleted: () => void;
  /** Called when the user clicks "Back to Reports" — parent routes to the home view. */
  onBack?: () => void;
  onOpenReport?: (reportId: number) => void;
}

/**
 * A created PnL report's detail view. Loads the saved definition, runs its pivot query,
 * and renders the scenario × period grid (tree-drillable, finance-formatted). The flat
 * long serving result is pivoted to wide client-side (see hooks/useStarGridPnlReport).
 */
export const ReportDetailView: React.FC<ReportDetailViewProps> = ({ reportId, onDeleted, onBack, onOpenReport }) => {
  const { pnlReports, pnlConfig, setTools, setToolsOpen } = useStarGridData();
  const text = useDataSyncText();
  const [scaleId, setScaleId] = useState<string>(PNL_DEFAULT_SCALE.id);

  // Load the picker config once — needed for human dimension labels on the pivot header.
  useEffect(() => {
    pnlConfig.ensureLoaded();
  }, [pnlConfig]);

  const dimensionLabels = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = { [PNL_STATEMENT_LINE_DIMENSION]: text('statementLine') };
    for (const d of pnlConfig.data?.available_dimensions ?? []) {
      const key = d.fact_key ?? d.hierarchy_id;
      if (key && d.label) map[key] = d.label;
    }
    return map;
  }, [pnlConfig.data, text]);

  const onReportDeleted = useCallback(() => {
    pnlReports.refresh();
    onDeleted();
  }, [pnlReports, onDeleted]);

  const {
    definition,
    model,
    response,
    loading,
    error,
    oversized,
    groupBy,
    filters,
    drillableDimensions,
    reload,
    drillDown,
    setFilters,
    resetDrill,
    asOfTimestamp,
    setVersion
  } = useStarGridPnlReport(reportId, onReportDeleted);
  const selectors = useReportSelectors({ definition, asOfTimestamp, onVersionChange: setVersion, onOpenReport });

  // ─── Tool Panel content ──────────────────────────────────────────────────────
  // Report Health & Stats block (ported from xPT PnlReportHealth) + the report's
  // definition + generated SQL. Rebuilt whenever the config or the last response
  // changes; cleared on unmount so other views don't inherit this one's panel.
  const infoPanel = useMemo(
    () => (
      <HelpPanel header={<h2>Report Health & Stats</h2>}>
        {definition?.report_description && <Box>{definition.report_description}</Box>}
        {definition ? <ReportHealth config={definition} /> : <Box color="text-body-secondary">Loading report health…</Box>}

        {definition ? (
          <>
            <div style={{ borderTop: '1px solid var(--color-border-divider-default, #d5dbdb)', margin: '16px 0' }} />
            <h3 style={{ marginTop: 0 }}>Definition</h3>
            <div style={{ marginBottom: 8 }}>
              <Box variant="awsui-key-label">Fact source</Box>
              <Badge>{definition.source_mode === 'EXTERNAL' ? 'External (cdp_lake Glue)' : 'Native (Ingested)'}</Badge>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Box variant="awsui-key-label">{`Drillable dimensions (${(definition.group_by_dimensions ?? []).length})`}</Box>
              <SpaceBetween direction="horizontal" size="xxs">
                {(definition.group_by_dimensions ?? []).map((d) => (
                  <Badge key={d}>{d}</Badge>
                ))}
              </SpaceBetween>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Box variant="awsui-key-label">{`Filter dimensions (${(definition.filter_by_dimension ?? []).length})`}</Box>
              {(definition.filter_by_dimension ?? []).length > 0 ? (
                <SpaceBetween direction="horizontal" size="xxs">
                  {(definition.filter_by_dimension ?? []).map((d) => (
                    <Badge key={d}>{d}</Badge>
                  ))}
                </SpaceBetween>
              ) : (
                <Box color="text-body-secondary">None.</Box>
              )}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Box variant="awsui-key-label">{`Scenarios (${(definition.scenarios ?? []).length})`}</Box>
              <SpaceBetween size="xxs">
                {(definition.scenarios ?? []).map((s, i) => (
                  <Box key={`${s.reporting_scenario_id}-${i}`}>
                    <Badge>{s.label}</Badge> · {s.months.length} months
                  </Box>
                ))}
              </SpaceBetween>
            </div>
          </>
        ) : null}

        {response?.sql ? (
          <>
            <div style={{ borderTop: '1px solid var(--color-border-divider-default, #d5dbdb)', margin: '16px 0' }} />
            <h3 style={{ marginTop: 0 }}>Generated SQL</h3>
            <Box variant="code">{response.sql}</Box>
          </>
        ) : null}
      </HelpPanel>
    ),
    [definition, response]
  );

  useEffect(() => {
    setTools(infoPanel);
    return () => setTools(null);
  }, [infoPanel, setTools]);

  return (
    <ReportLayout
      header={
        <>
          <h1>{definition?.report_name ?? `Report ${reportId}`}</h1>
          <div className="sg-report-layout-actions">
            <Button variant="icon" iconName="refresh" ariaLabel={text('refreshReport')} onClick={reload} loading={loading} />
            <Button variant="icon" iconName="arrow-left" ariaLabel={text('backToReports')} onClick={onBack} disabled={!onBack} />
          </div>
        </>
      }
      utility={
        <Link variant="info" onFollow={() => setToolsOpen(true)}>
          {text('reportHelp')}
        </Link>
      }
      filters={
        definition && (
          <ReportFilterBar
            selectors={selectors}
            definition={definition}
            filters={filters}
            dimensionLabels={dimensionLabels}
            scaleId={scaleId}
            onScaleChange={setScaleId}
            onFiltersChange={setFilters}
            onReset={resetDrill}
            disabled={loading || definition.status !== 'READY'}
          />
        )
      }
      notices={
        (error || oversized || definition?.status === 'GENERATING' || definition?.status === 'FAILED') && (
          <SpaceBetween size="s">
            {error && (
              <Alert type="error" header="Could Not Load Report">
                {error}
              </Alert>
            )}

            {definition?.status === 'GENERATING' && (
              <Alert type="info" header="Report Is Generating">
                <SpaceBetween size="xs">
                  <Box>{error ? text('reportPollingPaused') : text('reportGeneratingMessage')}</Box>
                  {definition.mv_refresh_state ? (
                    <Box variant="small" color="text-body-secondary">
                      StarRocks state: <code>{definition.mv_refresh_state}</code>
                      {definition.mv_last_refresh_duration ? <> · duration: {definition.mv_last_refresh_duration}s</> : null}
                    </Box>
                  ) : null}
                </SpaceBetween>
              </Alert>
            )}

            {definition?.status === 'FAILED' && (
              <Alert type="error" header="Report Generation Failed">
                {definition.mv_last_refresh_error || text('reportGenerationFailed')}
              </Alert>
            )}

            {oversized ? (
              <Alert type="warning" header="Result Too Large">
                <SpaceBetween size="xs">
                  <Box>
                    This drill would return approximately <strong>{oversized.estimatedRows.toLocaleString()}</strong> rows — over the{' '}
                    <strong>{oversized.rowLimit.toLocaleString()}</strong>-row guardrail.
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    {oversized.hint}
                  </Box>
                </SpaceBetween>
              </Alert>
            ) : null}
          </SpaceBetween>
        )
      }
    >
      {loading && !model ? (
        <Box textAlign="center" padding="l">
          <Spinner /> {text('reportLoading')}
        </Box>
      ) : (
        model && (
          <SpaceBetween size="s">
            <div className={styles.gridFrame}>
              <PnlReportGrid
                reportName={definition?.report_name}
                reportFamily={definition?.deck_report ?? definition?.report_name}
                model={model}
                loading={loading}
                scaleId={scaleId}
                groupBy={groupBy}
                drillableDimensions={drillableDimensions}
                onDrillDown={drillDown}
                dimensionLabels={dimensionLabels}
              />
            </div>
          </SpaceBetween>
        )
      )}
    </ReportLayout>
  );
};

export default ReportDetailView;
